import { useEffect, useMemo, useState } from "react";
import { fetchOpportunityCards, getAllMarketplaceCards } from "../services/api";
import { MarketplaceCardResponseDto, Page, ProfileId } from "../types/types";
import {
  OPPORTUNITY_PAGE_SIZE,
  getPageItems,
  getTotalPages,
  isSelectableCard,
} from "../utils/deals/dealModalHelpers";

export type OpportunityTab = "all" | "event";

// The marketplace API cannot filter by event date, so one large page is loaded
// and filtered/paginated here. A server-side event date filter would replace this.
const FETCH_SIZE = 100;

interface LoadedCards {
  key: string;
  cards: MarketplaceCardResponseDto[];
  failed: boolean;
}

const fetchTabCards = async (
  tab: OpportunityTab,
  fighterId?: ProfileId,
): Promise<MarketplaceCardResponseDto[]> => {
  if (tab === "all") {
    const data: Page<MarketplaceCardResponseDto> = await fetchOpportunityCards({
      page: 0,
      size: FETCH_SIZE,
      myCard: true,
      isDeal: true,
    });
    return data.content ?? [];
  }

  const result = await getAllMarketplaceCards(
    localStorage.getItem("username") || "",
    {
      fighterId: fighterId ? String(fighterId) : undefined,
      isPublished: true,
      isDeal: true,
      size: FETCH_SIZE,
    },
  );
  return result.content ?? [];
};

interface UseUpcomingOpportunityCardsOptions {
  tab: OpportunityTab;
  fighterId?: ProfileId;
  enabled?: boolean;
}

// Loads the published opportunities with an upcoming event for one tab and
// serves them page by page.
export const useUpcomingOpportunityCards = ({
  tab,
  fighterId,
  enabled = true,
}: UseUpcomingOpportunityCardsOptions) => {
  const requestKey = `${tab}:${fighterId ?? ""}`;
  const [loaded, setLoaded] = useState<LoadedCards | null>(null);
  const [page, setPage] = useState(0);

  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;
    setPage(0);

    fetchTabCards(tab, fighterId)
      .then(
        (cards) => ({
          cards: cards.filter((card) => isSelectableCard(card)),
          failed: false,
        }),
        () => ({ cards: [], failed: true }),
      )
      .then((outcome) => {
        if (!cancelled) {
          setLoaded({ key: requestKey, ...outcome });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, tab, fighterId, requestKey]);

  const isCurrent = loaded?.key === requestKey;
  const cards = useMemo(
    () => (isCurrent && loaded ? loaded.cards : []),
    [isCurrent, loaded],
  );
  const totalPages = getTotalPages(cards.length, OPPORTUNITY_PAGE_SIZE);
  const currentPage = Math.min(page, totalPages - 1);

  return {
    cards: getPageItems(cards, currentPage, OPPORTUNITY_PAGE_SIZE),
    totalCount: cards.length,
    page: currentPage,
    setPage,
    totalPages,
    isLoading: enabled && !isCurrent,
    hasError: isCurrent && Boolean(loaded?.failed),
  };
};
